/**
 * Hook unificado de ENTREGA multi-canal de notificações.
 *
 * Consome as preferências módulo×canal (matrix de `useNotificationSettings`)
 * e despacha cada nova `AgentNotification` recebida via Realtime para os
 * canais habilitados: `sound` (bip via WebAudio) e `browser` (Notification API).
 *
 * Canais `in_app` (toast/badge) e `push_fcm` continuam tratados por outros
 * componentes (toast do Sonner em `useAgentNotifications` e `fcm.ts`).
 *
 * Regras:
 *  - DND: se o canal estiver em `channels_silenced` e horário/dia atuais
 *    caem dentro da janela DND ativa, o canal é bloqueado silenciosamente.
 *  - `sound`: tenta inicializar/resumir AudioContext sob demanda; só toca
 *    de fato se o navegador autorizar (autoplay policy depende de gesto).
 *  - `browser`: dispara sempre que o canal estiver habilitado e a permissão
 *    estiver `granted`. Usa `tag` para deduplicar e não polui aba ativa.
 *
 * @version 1.2.0 - 2026-06-05 — Browser notifications via ServiceWorkerRegistration.showNotification()
 *                              (popup confiável mesmo com aba em foco; desktop + mobile).
 *                              Aviso único quando permissão = 'default'.
 * @version 1.1.0 - 2026-06-05 — Áudio resiliente (resume on demand) + browser
 *                              também notifica com aba visível.
 * @version 1.0.0 - 2026-06-05
 */
import { useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';
import { useNotificationSettings } from '@/hooks/useNotificationSettings';
import { mapTypeToModule } from '@/hooks/useNotificationCenter';
import type { AgentNotification } from '@/hooks/useAgentNotifications';

const logger = createLogger('useNotificationDelivery');

/**
 * Deriva a rota para abrir ao clicar no popup, com base no tipo da notificação.
 * Fallback seguro: dashboard do tenant.
 */
function deriveUrlFromNotification(n: AgentNotification): string {
  const meta = (n.metadata ?? {}) as Record<string, unknown>;
  switch (n.type) {
    case 'transfer_received':
    case 'auto_assigned':
    case 'department_transfer':
      return n.conversation_id ? `/tenant/chat?conversation=${n.conversation_id}` : '/tenant/chat';
    case 'activity_mention':
      return '/tenant/crm/activities';
    // v1.2.0: menção em mensagem privada abre a conversa correspondente
    case 'chat_note_mention':
      return n.conversation_id ? `/tenant/chat?conversation=${n.conversation_id}` : '/tenant/chat';
    case 'scheduling_booking':
      return meta.deal_id ? `/tenant/crm/deals?dealId=${meta.deal_id}` : '/tenant/scheduling';
    case 'contract_signed':
      return '/tenant/contracts';
    case 'payment_received':
      return '/tenant/financial/charges';
    case 'task_assigned':
    case 'task_comment_added':
      return meta.task_id ? `/tenant/tasks?taskId=${meta.task_id}` : '/tenant/tasks';
    case 'deal_seller_assigned':
      return meta.deal_id ? `/tenant/crm/deals?dealId=${meta.deal_id}` : '/tenant/crm/deals';
    default:
      return '/tenant';
  }
}

/** Mapeia módulos do mapTypeToModule para os módulos da matriz de preferências. */
const MODULE_MAP: Record<string, string> = {
  conversations: 'chat',
  mentions: 'activities',
  activities: 'activities',
  scheduling: 'scheduling',
  contracts: 'contracts',
  financial: 'financial',
  tasks: 'tasks',
  crm: 'crm',
};

/** Verifica se o horário atual está dentro da janela DND configurada. */
function isWithinDnd(
  dnd: { is_enabled: boolean; start_time: string; end_time: string; days_of_week: number[] } | null,
): boolean {
  if (!dnd || !dnd.is_enabled) return false;
  const now = new Date();
  // ISO day-of-week: 1=segunda ... 7=domingo
  const isoDow = ((now.getDay() + 6) % 7) + 1;
  if (!dnd.days_of_week.includes(isoDow)) return false;
  const [sh, sm] = dnd.start_time.split(':').map(Number);
  const [eh, em] = dnd.end_time.split(':').map(Number);
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const startMin = sh * 60 + sm;
  const endMin = eh * 60 + em;
  if (startMin <= endMin) {
    return nowMin >= startMin && nowMin < endMin;
  }
  // Janela cruza meia-noite
  return nowMin >= startMin || nowMin < endMin;
}

/**
 * Hook de entrega. Deve ser usado próximo ao consumidor Realtime
 * (ex.: dentro de `useAgentNotifications`) e a função `deliver` chamada
 * para cada notificação recém-chegada.
 */
export function useNotificationDelivery() {
  const { data, refetch } = useNotificationSettings();

  // Mantém uma ref do snapshot atual para que `deliver` (memoizado) sempre
  // enxergue a versão mais recente sem recriar callbacks.
  const settingsRef = useRef(data);
  useEffect(() => {
    settingsRef.current = data;
  }, [data]);

  // AudioContext único reutilizado por toda a sessão.
  const audioCtxRef = useRef<AudioContext | null>(null);

  /**
   * Garante que o AudioContext exista e esteja em estado `running`.
   * Pode ser chamado em qualquer momento — se o navegador exigir gesto,
   * o `resume()` apenas falha silenciosamente e voltamos a tentar depois.
   */
  const ensureAudioReady = useCallback(async (): Promise<boolean> => {
    try {
      if (typeof window === 'undefined') return false;
      const Ctor =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return false;
      if (!audioCtxRef.current) {
        audioCtxRef.current = new Ctor();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === 'suspended') {
        try {
          await ctx.resume();
        } catch (err) {
          logger.debug('AudioContext.resume() bloqueado (sem gesto ainda):', err);
        }
      }
      return ctx.state === 'running';
    } catch (err) {
      logger.warn('Falha ao preparar AudioContext:', err);
      return false;
    }
  }, []);

  // Desbloqueia o áudio assim que houver qualquer gesto do usuário.
  // Diferente da versão anterior, não usamos `{ once: true }` — mantemos
  // os listeners até confirmar que o contexto está rodando.
  useEffect(() => {
    const unlock = () => {
      void ensureAudioReady().then((ready) => {
        if (ready) {
          window.removeEventListener('pointerdown', unlock);
          window.removeEventListener('keydown', unlock);
          window.removeEventListener('touchstart', unlock);
        }
      });
    };
    window.addEventListener('pointerdown', unlock);
    window.addEventListener('keydown', unlock);
    window.addEventListener('touchstart', unlock);
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
      window.removeEventListener('touchstart', unlock);
    };
  }, [ensureAudioReady]);

  /** Toca bip curto agradável (2 tons descendentes) via WebAudio. */
  const playBeep = useCallback(async () => {
    const ready = await ensureAudioReady();
    if (!ready || !audioCtxRef.current) {
      logger.debug('Bip ignorado: AudioContext não disponível/desbloqueado.');
      return;
    }
    const ctx = audioCtxRef.current;
    const now = ctx.currentTime;
    const play = (freq: number, start: number, dur: number) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, now + start);
      gain.gain.linearRampToValueAtTime(0.18, now + start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now + start);
      osc.stop(now + start + dur);
    };
    play(880, 0, 0.18);
    play(660, 0.12, 0.22);
  }, [ensureAudioReady]);

  // Controla aviso único por sessão quando permissão = 'default'
  const permissionPromptedRef = useRef(false);

  /**
   * Dispara notificação nativa do navegador.
   *
   * v1.2.0: usa preferencialmente `ServiceWorkerRegistration.showNotification()`.
   * Motivo: `new Notification()` no Chrome desktop frequentemente não exibe o
   * toast do SO quando a aba que criou está em foco — a notificação vai
   * silenciosamente para o centro do sistema. A API do SW exibe o popup do
   * SO de forma consistente, em desktop e mobile, mesmo com a aba visível,
   * e ainda permite roteamento no clique via handler `notificationclick`
   * já existente em `public/firebase-messaging-sw.js`.
   */
  const showBrowserNotification = useCallback(async (n: AgentNotification) => {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      logger.debug('Notification API indisponível.');
      return;
    }

    // Permissão ainda não decidida → solicita uma única vez por sessão via toast
    if (Notification.permission === 'default') {
      if (!permissionPromptedRef.current) {
        permissionPromptedRef.current = true;
        toast.info('Ative as notificações do navegador para receber popups.', {
          duration: 8000,
          action: {
            label: 'Ativar',
            onClick: () => {
              void Notification.requestPermission().catch(() => undefined);
            },
          },
        });
      }
      return;
    }

    if (Notification.permission !== 'granted') {
      logger.debug('Permissão de notificação não concedida:', Notification.permission);
      return;
    }

    const targetUrl = deriveUrlFromNotification(n);
    const title = n.title || 'Notificação';
    const options: NotificationOptions & { renotify?: boolean } = {
      body: n.message || '',
      icon: '/pwa-192x192.png',
      badge: '/pwa-192x192.png',
      tag: n.id,
      renotify: true,
      requireInteraction: false,
      data: { url: targetUrl, notificationId: n.id },
    };

    // Caminho preferido: Service Worker (popup do SO mesmo com aba em foco)
    try {
      if ('serviceWorker' in navigator) {
        const reg = await navigator.serviceWorker.getRegistration();
        if (reg && typeof reg.showNotification === 'function') {
          await reg.showNotification(title, options);
          logger.debug('Browser notification via SW:', { id: n.id, url: targetUrl });
          return;
        }
      }
    } catch (err) {
      logger.warn('Falha ao exibir via ServiceWorker, tentando fallback:', err);
    }

    // Fallback: construtor Notification (pode ser silencioso em aba ativa)
    try {
      const notif = new Notification(title, options);
      notif.onclick = () => {
        try { window.focus(); } catch { /* ignore */ }
        notif.close();
        window.dispatchEvent(new CustomEvent('notification:open', { detail: n }));
      };
      logger.debug('Browser notification via Notification() fallback:', { id: n.id });
    } catch (err) {
      logger.warn('Falha ao exibir Notification do navegador:', err);
    }
  }, []);

  /** Entrega uma notificação respeitando preferências e DND. */
  const deliver = useCallback((notification: AgentNotification) => {
    const snapshot = settingsRef.current;
    if (!snapshot) {
      logger.debug('Snapshot de preferências ainda não carregado; pulando entrega.');
      return;
    }

    const rawModule = mapTypeToModule(notification.type);
    const module = MODULE_MAP[rawModule] ?? rawModule;

    const prefMap = new Map<string, boolean>();
    for (const p of snapshot.preferences) {
      prefMap.set(`${p.module}::${p.channel_code}`, p.is_enabled);
    }
    // Default: implementados ligados (espelha matrix em useNotificationSettings)
    const isEnabled = (channel: string) => {
      const k = `${module}::${channel}`;
      if (prefMap.has(k)) return prefMap.get(k)!;
      return ['in_app', 'sound', 'browser', 'push_fcm'].includes(channel);
    };

    const dndActive = isWithinDnd(snapshot.dnd);
    const silenced = (channel: string) =>
      dndActive && (snapshot.dnd?.channels_silenced ?? []).includes(channel);

    // Backend pode marcar explicitamente play_sound=false (futuro).
    const metaPlaySound = (notification.metadata as Record<string, unknown> | null | undefined)?.play_sound;
    const soundAllowedByBackend = metaPlaySound !== false;

    const soundDecision = isEnabled('sound') && !silenced('sound') && soundAllowedByBackend;
    const browserDecision = isEnabled('browser') && !silenced('browser');

    logger.debug('Entrega multi-canal solicitada:', {
      id: notification.id,
      type: notification.type,
      module,
      sound: soundDecision,
      browser: browserDecision,
      dndActive,
      backendPlaySound: metaPlaySound,
      hidden: typeof document !== 'undefined' ? document.hidden : null,
      notificationPermission: typeof Notification !== 'undefined' ? Notification.permission : 'n/a',
    });

    // Som na aba (assíncrono, mas sem await para não atrasar UI)
    if (soundDecision) {
      void playBeep();
    }

    // Notificação do navegador
    if (browserDecision) {
      showBrowserNotification(notification);
    }
  }, [playBeep, showBrowserNotification]);

  /** Solicita permissão do navegador (reutilizável pela UI). */
  const requestBrowserPermission = useCallback(async (): Promise<NotificationPermission> => {
    if (!('Notification' in window)) return 'denied';
    if (Notification.permission === 'granted') return 'granted';
    try {
      return await Notification.requestPermission();
    } catch (err) {
      logger.warn('Falha ao solicitar permissão de notificação:', err);
      return 'denied';
    }
  }, []);

  return {
    deliver,
    requestBrowserPermission,
    browserPermission: typeof Notification !== 'undefined' ? Notification.permission : 'denied' as NotificationPermission,
    refetchSettings: refetch,
    ensureAudioReady,
  };
}
